import Header from "@/components/Header";
import Footer from "@/components/Footer";

const CARD = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" };

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl p-5 space-y-3" style={CARD}>
      <h2 className="text-white font-bold text-[15px]">{title}</h2>
      <div className="text-slate-400 text-[13px] leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

export default function Terms() {
  return (
    <div className="ls-shell min-h-screen flex flex-col">
      <Header />

      <div className="px-5 pt-8 pb-5" style={{ background: "linear-gradient(180deg,#041a2e 0%,#030a14 100%)" }}>
        <div className="max-w-3xl mx-auto">
          <p className="text-slate-500 text-[11px] uppercase tracking-widest font-bold mb-2">LiveSwell</p>
          <h1 className="text-white font-black text-2xl leading-tight">Terms of Service</h1>
          <p className="text-slate-500 text-[12px] mt-1">Effective date: July 1, 2025 &nbsp;·&nbsp; Last updated: October 9, 2026</p>
        </div>
      </div>

      <main className="flex-1 px-4 pt-5 pb-10 max-w-3xl mx-auto w-full space-y-3">

        <Section title="Acceptance of terms">
          <p>
            By accessing or using LiveSwell ("the Service"), you agree to be bound by these Terms of
            Service. If you do not agree, please do not use the Service.
          </p>
        </Section>

        <Section title="Monthly plans, trials, and billing">
          <p>LiveSwell Pro is $8 USD per month and Agent is $20 USD per month, plus applicable taxes. Eligible new subscribers may receive a seven-day trial; trial availability is determined at checkout and repeat trials are not promised. After any trial, the selected plan renews monthly until canceled. Paddle processes web subscriptions and payment details. Cancel through Account before the next renewal to stop a future renewal.</p>
          <p>A renewal payment failure on a previously paid subscription receives a 72-hour grace period. This grace applies only to a confirmed previously paid renewal, not an initial payment or a payment due when a trial ends.</p>
          <p>In the native iOS purchase flow, Pro-to-Agent upgrades are prorated. For a custom Paddle Agent-to-Pro downgrade, Agent access is retained through the original paid boundary. Paddle may replace the subscription item earlier; LiveSwell's one-hour application cutoff is an application behavior and is not a guarantee of Paddle timing.</p>
        </Section>

        <Section title="Agent membership and messaging">
          <p>Agent includes worldwide condition data, up to five active alert settings with email and SMS, and up to five automated alert texts each day. Each matching alert can send at most once per day; the daily automated-text allowance resets at 00:01 in your home time zone.</p>
          <p>Agent includes 120 outgoing conversational Agent SMS replies per billing month. Incoming texts do not count toward that allowance, and automated alert texts are separate. Collection for extra outgoing Agent SMS replies is currently disabled. Historical reply usage and billing records may remain visible in account history. In-app Agent chat is subject to fair-use rate limits.</p>
        </Section>

        <Section title="Description of service">
          <p>
            LiveSwell provides real-time surf condition data including wave height, wind, and tide
            information for surf spots worldwide. The Service includes optional SMS and email alert
            notifications and optional LiveSwell Agent replies to surf questions in the app or by
            text, subject to the applicable membership and messaging limits.
          </p>
        </Section>

        <Section title="User accounts">
          <p>
            You are responsible for maintaining the confidentiality of your account credentials.
            You must provide accurate information when creating your account. You may not use the
            Service for any unlawful purpose.
          </p>
        </Section>

        <Section title="SMS messaging terms">
          <p>
            By opting in to SMS alerts, you agree to receive automated text messages from LiveSwell
            at the phone number you provide. Message types include surf condition alerts, daily
            digests, and, when you choose to text a surf question to the LiveSwell Agent, automated
            responses powered by Anthropic's Claude.
          </p>
          <p><strong className="text-slate-300">Message frequency:</strong> Varies based on conditions and your alert settings. You may receive multiple messages per day when surf conditions change.</p>
          <p><strong className="text-slate-300">Message and data rates may apply.</strong> Contact your wireless carrier for plan details.</p>
          <p><strong className="text-slate-300">To opt out:</strong> Reply STOP to any SMS from LiveSwell to stop SMS delivery, or reply HELP for assistance. A carrier-level START reply does not automatically restore your LiveSwell alert settings; review Notification Settings before expecting alerts to resume.</p>
          <p>
            SMS consent is not required to use other features of the Service and is not a condition
            of any purchase.
          </p>
        </Section>

        <Section title="Surf data accuracy">
          <p>
            Surf condition data is sourced from NOAA, OpenWeatherMap, and other third-party providers.
            LiveSwell does not guarantee the accuracy, completeness, or timeliness of this data.
            Always use your own judgment before entering the water. LiveSwell is not liable for
            decisions made based on the data provided.
          </p>
        </Section>

        <Section title="LiveSwell Agent SMS responses">
          <p>
            When you text the LiveSwell Agent with a surf question, optional SMS answers are generated
            by Anthropic's Claude from a minimal portion of your recent conversation and relevant spot
            or conditions data. LiveSwell does not send your phone number or account identifier to
            Anthropic for this generation. Responses are automated, may be incomplete or inaccurate,
            and may not reflect real-time conditions. They provide no safety guarantee and must not
            be relied on for ocean safety decisions.
          </p>
        </Section>

        <Section title="Intellectual property">
          <p>
            All content, design, and software comprising the Service is owned by LiveSwell and
            protected by applicable intellectual property laws. You may not copy, reproduce, or
            redistribute any part of the Service without written permission.
          </p>
        </Section>

        <Section title="Limitation of liability">
          <p>
            To the fullest extent permitted by law, LiveSwell shall not be liable for any indirect,
            incidental, special, or consequential damages arising from your use of the Service,
            including but not limited to damages related to surf conditions data or SMS communications.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p>
            We may update these Terms of Service from time to time. Continued use of the Service
            after changes are posted constitutes acceptance of the updated terms.
          </p>
        </Section>

        <Section title="Contact">
          <p>
            Questions about these terms? Contact us at{" "}
            <a href="mailto:legal@liveswell.io" className="text-emerald-400 underline hover:text-emerald-300">
              legal@liveswell.io
            </a>{" "}or visit{" "}
            <a href="https://liveswell.io" className="text-emerald-400 underline hover:text-emerald-300">
              liveswell.io
            </a>.
          </p>
        </Section>

      </main>

      <Footer />
    </div>
  );
}
